import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  FiSend,
  FiUser,
  FiTag,
  FiBriefcase,
  FiCalendar,
  FiCheckSquare,
  FiFileText,
  FiEdit2,
} from 'react-icons/fi';

import Drawer from '../../components/ui/Drawer';
import Button from '../../components/ui/Button';
import StatusPill from '../../components/ui/StatusPill';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ErrorBanner from '../../components/ui/ErrorBanner';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import DocumentList from '../../components/ui/DocumentList';
import WorkflowTimeline from '../../components/ui/WorkflowTimeline';
import WorkflowActions from '../../components/ui/WorkflowActions';
import CurrencyDisplay from '../../components/shared/CurrencyDisplay';
import DateDisplay from '../../components/shared/DateDisplay';

import { getReimbursement, submitReimbursement, updateReimbursement } from '../../api/reimbursements';
import { getWorkflowForEntity } from '../../api/workflows';
import { extractErrorMessage } from '../../api/client';
import { WorkflowStatus } from '../../enums';
import { useAuth } from '../../auth/AuthContext';
import ReimbursementFormInner from './ReimbursementFormInner';

function MetaItem({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-md bg-slate-100 text-ink-muted">
        <Icon size={14} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-medium uppercase tracking-wide text-ink-muted">
          {label}
        </div>
        <div className="mt-0.5 text-sm text-ink">{children}</div>
      </div>
    </div>
  );
}

export default function ReimbursementDetailDrawer({
  open,
  reimbursementId,
  fallback,
  onClose,
}) {
  const qc = useQueryClient();
  const { user, isAdmin } = useAuth();
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editErrors, setEditErrors] = useState({});

  const {
    data: fetched,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['reimbursements', reimbursementId],
    queryFn: () => getReimbursement(reimbursementId),
    enabled: !!reimbursementId && open,
    initialData:
      fallback && fallback.id === reimbursementId ? fallback : undefined,
  });

  const reimbursement =
    fetched || (fallback?.id === reimbursementId ? fallback : null);

  // Workflow lookup is only meaningful once the claim has left DRAFT.
  const workflowEnabled =
    !!reimbursementId &&
    open &&
    !!reimbursement &&
    reimbursement.workflowStatus !== WorkflowStatus.DRAFT;

  const { data: workflow } = useQuery({
    queryKey: ['workflows', 'REIMBURSEMENT', reimbursementId],
    queryFn: () => getWorkflowForEntity('REIMBURSEMENT', reimbursementId),
    enabled: workflowEnabled,
  });

  React.useEffect(() => {
    if (reimbursement && isEditing) {
      setEditForm({
        title: reimbursement.title || '',
        description: reimbursement.description || '',
        amount: reimbursement.amount || '',
        category: reimbursement.category || 'TRAVEL',
        department: reimbursement.department || 'HR',
        paymentMode: reimbursement.paymentMode || 'BANK_TRANSFER',
        transactionDate: reimbursement.transactionDate || '',
      });
      setEditErrors({});
    }
  }, [reimbursement, isEditing]);

  const updateMutation = useMutation({
    mutationFn: (payload) => updateReimbursement(reimbursementId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reimbursements'] });
      qc.invalidateQueries({ queryKey: ['reimbursements', reimbursementId] });
      toast.success('Reimbursement updated successfully');
      setIsEditing(false);
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  function validateEdit() {
    const next = {};
    if (!editForm.title?.trim()) next.title = 'Title is required';
    if (!editForm.amount || Number(editForm.amount) <= 0)
      next.amount = 'Amount must be greater than zero';
    if (!editForm.category?.trim()) next.category = 'Category is required';
    setEditErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSaveEdit() {
    if (!validateEdit()) return;
    updateMutation.mutate({
      title: editForm.title.trim(),
      description: editForm.description.trim() || null,
      amount: Number(editForm.amount),
      category: editForm.category,
      department: editForm.department || null,
      paymentMode: editForm.paymentMode || null,
      transactionDate: editForm.transactionDate || null,
    });
  }

  const submitMutation = useMutation({
    mutationFn: () => submitReimbursement(reimbursementId),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['reimbursements'] });
      qc.invalidateQueries({ queryKey: ['reimbursements', reimbursementId] });
      qc.invalidateQueries({ queryKey: ['workflows'] });
      toast.success(`Claim ${data?.title || ''} submitted for approval`);
      setConfirmSubmit(false);
    },
    onError: (e) => {
      toast.error(extractErrorMessage(e));
      setConfirmSubmit(false);
    },
  });

  const status = reimbursement?.workflowStatus;
  const isDraft = status === WorkflowStatus.DRAFT;

  const isOwner =
    !!user &&
    !!reimbursement &&
    (Number(user.id) === Number(reimbursement.employeeId) ||
      user.username === reimbursement.createdBy);

  const canSubmit = isDraft && (isOwner || isAdmin);
  const canUploadDocs = (isDraft && isOwner) || isAdmin;
  const canEdit = isDraft && (isOwner || isAdmin);

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        width="lg"
        title={
          reimbursement
            ? reimbursement.title || `Claim #${reimbursement.id}`
            : 'Reimbursement'
        }
        footer={
          reimbursement ? (
            isEditing ? (
              <div className="flex w-full items-center justify-end gap-2">
                <Button variant="ghost" onClick={() => setIsEditing(false)} disabled={updateMutation.isPending}>
                  Cancel
                </Button>
                <Button variant="primary" loading={updateMutation.isPending} onClick={handleSaveEdit}>
                  Save Changes
                </Button>
              </div>
            ) : (
              <div className="flex w-full items-center justify-start gap-2">
                {canSubmit && (
                  <Button
                    variant="primary"
                    icon={FiSend}
                    onClick={() => setConfirmSubmit(true)}
                    loading={submitMutation.isPending}
                  >
                    Submit for approval
                  </Button>
                )}
                {canEdit && (
                  <Button
                    variant="ghost"
                    icon={FiEdit2}
                    onClick={() => setIsEditing(true)}
                  >
                    Edit
                  </Button>
                )}
              </div>
            )
          ) : null
        }
      >
        {isLoading && !reimbursement ? (
          <LoadingSpinner label="Loading claim..." />
        ) : error && !reimbursement ? (
          <ErrorBanner
            error={error}
            title="Failed to load reimbursement"
            onRetry={refetch}
          />
        ) : !reimbursement ? (
          <div className="py-10 text-center text-sm text-ink-muted">
            Reimbursement not found.
          </div>
        ) : isEditing ? (
          <ReimbursementFormInner
            form={editForm}
            update={(patch) => setEditForm((f) => ({ ...f, ...patch }))}
            errors={editErrors}
            isEdit={true}
          />
        ) : (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-gradient-to-br from-primary/5 to-accent/5 p-4">
              <div className="min-w-0">
                <div className="text-[10px] font-medium uppercase tracking-wide text-ink-muted">
                  Claim
                </div>
                <div className="truncate text-lg font-semibold text-ink">
                  {reimbursement.title || `#${reimbursement.id}`}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-[10px] font-medium uppercase tracking-wide text-ink-muted">
                    Amount
                  </div>
                  <div className="text-lg font-semibold text-primary">
                    <CurrencyDisplay value={reimbursement.amount} />
                  </div>
                </div>
                <StatusPill value={reimbursement.workflowStatus} />
              </div>
            </div>

            {reimbursement.description && (
              <section className="rounded-lg border border-slate-200 bg-white p-4">
                <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-muted">
                  Description
                </h3>
                <p className="whitespace-pre-line text-sm text-ink">
                  {reimbursement.description}
                </p>
              </section>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <MetaItem icon={FiTag} label="Category">
                {reimbursement.category
                  ? String(reimbursement.category).replace(/_/g, ' ')
                  : '—'}
              </MetaItem>
              <MetaItem icon={FiBriefcase} label="Department">
                {reimbursement.department || '—'}
              </MetaItem>
              <MetaItem icon={FiUser} label="Employee">
                {reimbursement.employeeUsername || (reimbursement.employeeId != null ? `#${reimbursement.employeeId}` : '—')}
              </MetaItem>
              <MetaItem icon={FiUser} label="Created By">
                {reimbursement.createdByUsername || reimbursement.createdBy || '—'}
              </MetaItem>
              <MetaItem icon={FiCalendar} label="Created">
                <DateDisplay value={reimbursement.createdAt} />
              </MetaItem>
              <MetaItem icon={FiCheckSquare} label="Approved By">
                {reimbursement.approvedByUsername || reimbursement.approvedBy || '—'}
              </MetaItem>
            </div>

            {workflow && (
              <section className="rounded-lg border border-slate-200 bg-white p-4">
                <WorkflowTimeline workflow={workflow} />
                {isAdmin && (
                  <div className="mt-4 flex justify-end">
                    <WorkflowActions
                      workflow={workflow}
                      invalidateKeys={[
                        ['reimbursements'],
                        ['reimbursements', reimbursementId],
                      ]}
                      showFor="admin"
                    />
                  </div>
                )}
              </section>
            )}

            {!workflow && isDraft && (
              <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-xs text-ink-muted">
                <FiFileText className="mr-1 inline" />
                This claim is still a draft. Submit it to start the approval
                workflow.
              </div>
            )}

            <DocumentList
              entityType="REIMBURSEMENT"
              entityId={reimbursement.id}
              department="HR"
              canUpload={canUploadDocs}
              canDelete={isAdmin || (isDraft && isOwner)}
            />
          </div>
        )}
      </Drawer>

      <ConfirmDialog
        open={confirmSubmit}
        title="Submit claim for approval?"
        message={`This will move "${
          reimbursement?.title || 'this claim'
        }" out of draft and route it through the approval workflow.`}
        confirmLabel="Submit"
        variant="primary"
        loading={submitMutation.isPending}
        onConfirm={() => submitMutation.mutate()}
        onClose={() => setConfirmSubmit(false)}
      />
    </>
  );
}
